/**
 * This component let a user log in to the backoffice or to the app
 * Flow:
 * 1. User enters email, then password directly
 * 2. If forgot password: User receives OTP, verifies, and sets new password
 * 3. If new user: the verified email is handed over to the signup page, which
 *    collects the phone, password and — optionally — a company. With one it
 *    opens the onboarding; without one it lands on the invitation the account
 *    was made to accept, or on /start.
 */

import { CardFooter, CardHeader, CardSubTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/features/utils/i18n";
import { toast } from "sonner";
import { EmailPasswordChallenge } from "./challenges/email-password";
import { WebauthnChallenge } from "./challenges/webauthn";

export const LoginForm = ({
  autoWebauthnUserId,
  hideWebauthn,
}: {
  autoWebauthnUserId?: string; // If set, will automatically trigger webauthn login for this user id
  hideWebauthn?: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  // Only our own sign-in, never `useAuth().isLoading`: that one also covers the
  // profile query, which react-query refetches when the window regains focus.
  // With an expired token left in storage that query has no data, so the
  // refetch reads as a first load — and the spinner below unmounted the form,
  // sending someone back from their e-mail with the code onto the e-mail step.
  // The page already waits for the first load (`isInitializing`, ./index.tsx).
  const [isLoading, setIsLoading] = useState(false);

  const [autoWebauthnAlreadyUsed, setAutoWebauthnAlreadyUsed] = useState(false);
  const runAutoChallenge = !!autoWebauthnUserId && !autoWebauthnAlreadyUsed;

  // Handle password verification success (login)
  const handlePasswordSuccess = async (passwordToken: string) => {
    setIsLoading(true);
    try {
      await login([passwordToken]);
    } catch (e) {
      console.error(e);
      toast.error(t("loginFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle new user signup flow: the email is already verified, the signup page
  // picks it up from there and skips its own code step.
  const handleSignupFlow = (email: string, emailToken: string) => {
    navigate("/signup", {
      state: { email, emailPreSignedToken: emailToken },
    });
  };

  return (
    <>
      {!!isLoading && (
        <div className="flex w-full justify-center py-4">
          <Spinner />
        </div>
      )}

      {!isLoading && (
        <>
          {!runAutoChallenge && !isAuthenticated && (
            <>
              <EmailPasswordChallenge
                onSuccess={handlePasswordSuccess}
                onSignup={handleSignupFlow}
              />
            </>
          )}
          {!hideWebauthn && (
            <>
              {!runAutoChallenge && <Separator className="mt-4" />}
              <CardHeader className="block mt-2 -mb-4">
                <CardSubTitle>{t("sections.useAccessKey")}</CardSubTitle>
              </CardHeader>
              <WebauthnChallenge
                autoChallenge={runAutoChallenge}
                userIdOrEmail={autoWebauthnUserId}
                onEndChallenge={() => {
                  setAutoWebauthnAlreadyUsed(true);
                }}
                onSuccess={async (token) => {
                  setIsLoading(true);
                  try {
                    await login([token]);
                  } catch (e) {
                    console.error(e);
                    toast.error(t("loginFailed"));
                  } finally {
                    setIsLoading(false);
                  }
                }}
              />
            </>
          )}
          {!isAuthenticated && (
            <CardFooter className="flex flex-col">
              <Button
                type="button"
                variant="link"
                className="p-0"
                onClick={() => navigate("/signup")}
              >
                {t("signup.noAccountYet")}
              </Button>
            </CardFooter>
          )}
        </>
      )}
    </>
  );
};
